import { useMemo } from 'react';
import { useAppLocale } from '../providers/LocaleProvider';
import {
  pickLocalized,
  pickLocalizedCategoryName,
  pickLocalizedDescription,
  pickLocalizedTagPublishedDescription,
  pickLocalizedTagPublishedName,
  pickLocalizedTitle,
  pickBlockTitle,
  pickBlockTextContent,
  pickBlockTextFormatting,
  pickBlockTips,
  pickBlockStickyAudio,
  pickLibraryBookAuthor,
  pickLibraryBookTitle,
  pickQuestionExplanation,
  localizeQuizExerciseQuestion,
} from '../utils/localizedContent';

export function useLocalizedContent(options?: { forceEnglish?: boolean }) {
  const { locale: userLocale } = useAppLocale();
  const locale = options?.forceEnglish ? 'en' : userLocale;

  return useMemo(
    () => ({
      locale,
      pick: (english?: string | null, persian?: string | null) => pickLocalized(english, persian, locale),
      pickTitle: (item: Parameters<typeof pickLocalizedTitle>[0]) => pickLocalizedTitle(item, locale),
      pickDescription: (item: Parameters<typeof pickLocalizedDescription>[0]) =>
        pickLocalizedDescription(item, locale),
      pickBookTitle: (book: Parameters<typeof pickLibraryBookTitle>[0]) =>
        pickLibraryBookTitle(book, locale),
      pickBookAuthor: (book: Parameters<typeof pickLibraryBookAuthor>[0]) =>
        pickLibraryBookAuthor(book, locale),
      pickCategoryName: (item: Parameters<typeof pickLocalizedCategoryName>[0]) =>
        pickLocalizedCategoryName(item, locale),
      pickTagName: (item: Parameters<typeof pickLocalizedTagPublishedName>[0]) =>
        pickLocalizedTagPublishedName(item, locale),
      pickTagDescription: (item: Parameters<typeof pickLocalizedTagPublishedDescription>[0]) =>
        pickLocalizedTagPublishedDescription(item, locale),
      pickBlockTitle: (block: Parameters<typeof pickBlockTitle>[0]) => pickBlockTitle(block, locale),
      pickBlockText: (
        block: Parameters<typeof pickBlockTextContent>[0],
        formattingBlock?: Parameters<typeof pickBlockTextFormatting>[0]
      ) => ({
        text: pickBlockTextContent(block, locale),
        formatting: formattingBlock
          ? pickBlockTextFormatting(formattingBlock, locale)
          : pickBlockTextFormatting(block, locale),
      }),
      pickBlockTips: (block: Parameters<typeof pickBlockTips>[0]) => pickBlockTips(block, locale),
      pickBlockStickyAudio: (block: Parameters<typeof pickBlockStickyAudio>[0]) =>
        pickBlockStickyAudio(block),
      pickQuestionExplanation: (item: Parameters<typeof pickQuestionExplanation>[0]) =>
        pickQuestionExplanation(item, locale),
      localizeQuestion: <T extends Record<string, unknown>>(question: T) =>
        localizeQuizExerciseQuestion(question, locale),
    }),
    [locale, userLocale, options?.forceEnglish]
  );
}
